import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Fact } from './types';
import { useAuth } from '../../context/AuthContext';
import { setUserProgress } from '../../config/api';
import { SetUserProgressRequest } from '../../types/progress';
import { motion, useAnimationControls } from 'framer-motion';
import { Zap } from 'lucide-react';
import { cn } from '../../lib/utils';
import Timer from '../ui/Timer';
import { TouchpadInput, hasTouchScreen } from '../ui/TouchpadInput';
import { BackButton } from '../ui/BackButton';
import { DEFAULT_TRACK } from '../../types/constants';
import { playCorrectSound } from '../FluencyDrill/sounds';
import confetti from 'canvas-confetti';
import {
  getAccommodationMasteryThreshold,
  getAccommodationTimerDuration,
  isAccommodationTimerUntimed,
  getUpdatedDemonstrationCount
} from '../../policies/studentAccommodationPolicy';

// Utility functions
const renderOperationSymbol = (operation: Fact['operation']) => {
  switch (operation) {
    case 'addition': return '+';
    case 'subtraction': return '−';
    case 'multiplication': return '×';
    case 'division': return '÷';
  }
};

const isCommutativeOperation = (operation: Fact['operation']) => {
  return operation === 'addition' || operation === 'multiplication';
};

const getFactId = (fact: Fact, isInverted = false) => {
  const { operand1, operand2, operation } = fact;
  return isInverted ?
    `${operand2}-${operand1}-${operation}` :
    `${operand1}-${operand2}-${operation}`;
};

const getTrackingFactId = (fact: Fact) => {
  const baseFactId = fact.PK.split('FACT#')[1];
  const isInverted = fact.operand1 > fact.operand2;
  return isInverted ? `${baseFactId}i` : baseFactId;
};

// Fact mastery utilities
const isMastered = (correctAnswers: number, masteryThreshold: number) => correctAnswers >= masteryThreshold;

const getTimerDuration = (correctAnswers: number) => {
  // Use fixed time thresholds
  if (correctAnswers <= 0) return 6;
  if (correctAnswers === 1) return 4.5;
  if (correctAnswers >= 2) return 3;
  return 6; // Fallback
};

const getCardColor = (correctAnswers: number, masteryThreshold: number) => {
  if (correctAnswers >= masteryThreshold) return 'bg-green-100 border-green-500';
  if (correctAnswers === 2) return 'bg-green-50 border-green-300';
  if (correctAnswers === 1) return 'bg-yellow-100 border-yellow-500';
  if (correctAnswers === 0) return 'bg-white border-gray-200';
  return 'bg-red-100 border-red-400'; // for negative values
};

interface PermutatedFact extends Fact {
  id: string;
  correctAnswers: number;
}

// Generate all permutations of facts with initial correctAnswers
const generatePermutations = (facts: Fact[]): PermutatedFact[] => {
  return facts.flatMap(fact => {
    const permutations: PermutatedFact[] = [];

    if (isCommutativeOperation(fact.operation)) {
      // Commutative operations: a + b and b + a
      permutations.push(
        {
          ...fact,
          id: getFactId(fact),
          correctAnswers: 0
        },
        {
          ...fact,
          operand1: fact.operand2,
          operand2: fact.operand1,
          id: getFactId(fact, true),
          correctAnswers: 0
        }
      );
    } else {
      // Non-commutative operations: only original form
      permutations.push({
        ...fact,
        id: getFactId(fact),
        correctAnswers: 0
      });
    }

    return permutations;
  });
};

const FactMasteryCard: React.FC<{
  fact: PermutatedFact;
  masteryThreshold: number;
}> = ({ fact, masteryThreshold }) => {
  return (
    <div
      className={cn(
        "w-20 h-12 rounded-xl border-2 shadow-sm flex items-center justify-center transition-colors duration-300",
        getCardColor(fact.correctAnswers, masteryThreshold)
      )}
    >
      <span className="text-sm font-medium">
        {fact.operand1} {renderOperationSymbol(fact.operation)} {fact.operand2}
      </span>
    </div>
  );
};

const INDIGO_COLORS = ['#6366f1', '#818cf8', '#a78bfa', '#c084fc', '#e879f9'];

export function TimedPracticePage() {
  const { user } = useAuth();
  const masteryThreshold = getAccommodationMasteryThreshold(user?.iepAccommodationsEnabled);
  const isUntimed = isAccommodationTimerUntimed(user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const location = useLocation();
  const navigate = useNavigate();
  const originalFacts: Fact[] = location.state?.facts || [];
  const inputRef = useRef<HTMLInputElement>(null);
  const trackId = sessionStorage.getItem('activeTrackId') || DEFAULT_TRACK;

  // States
  const [userAnswer, setUserAnswer] = useState('');
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<PermutatedFact | null>(null);
  const [remainingQuestions, setRemainingQuestions] = useState<PermutatedFact[]>([]);
  const [masteredQuestions, setMasteredQuestions] = useState<Set<string>>(new Set());
  const [showingCorrectAnswer, setShowingCorrectAnswer] = useState(false);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [factAttempts, setFactAttempts] = useState<Record<string, {
    attempts: number;
    correct: number;
    timeSpent: number;
  }>>({});
  const [factProgress, setFactProgress] = useState<Record<string, number>>({});
  const factProgressRef = useRef<Record<string, number>>({});
  const [key, setKey] = useState(0);
  const isSubmitting = useRef<boolean>(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isInitialized, setIsInitialized] = useState(true); // Set to true by default since we don't need to fetch target time
  const [currentTimerZone, setCurrentTimerZone] = useState<'green' | 'yellow'>('green');
  const cardControls = useAnimationControls();

  // Initialize practice session
  useEffect(() => {
    const allPermutations = generatePermutations(originalFacts);
    const randomIndex = Math.floor(Math.random() * allPermutations.length);
    const firstQuestion = allPermutations[randomIndex];
    const initialRemaining = allPermutations.filter((_, index) => index !== randomIndex);

    setCurrentQuestion(firstQuestion);
    setRemainingQuestions(initialRemaining);
    setIsInitialized(true);
  }, []);

  // Focus input on mount and question change
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentQuestion]);

  // Helper function to check if both forms of a fact are ready for status update
  const checkBothFactFormsReady = (fact: PermutatedFact, progress: Record<string, number>): boolean => {
    // Get IDs for both original and inverted forms
    const originalId = getFactId(fact, false);
    const invertedId = getFactId({
      ...fact,
      operand1: fact.operand2,
      operand2: fact.operand1
    }, true);

    // Check if both have reached the student's configured mastery threshold.
    const originalCorrectAnswers = progress[originalId] || 0;
    const invertedCorrectAnswers = progress[invertedId] || 0;

    return originalCorrectAnswers >= masteryThreshold && invertedCorrectAnswers >= masteryThreshold;
  };

  // Add progress update when answering questions
  const updateProgressForAnswer = async (
    question: PermutatedFact,
    factId: string,
    previousCorrectAnswers: number,
    newCorrectAnswers: number,
    updatedProgress: Record<string, number>
  ) => {
    if (!user?.userId || newCorrectAnswers !== masteryThreshold || previousCorrectAnswers >= masteryThreshold) return;

    const shouldUpdateStatus = isCommutativeOperation(question.operation)
      ? checkBothFactFormsReady(question, updatedProgress)
      : true;

    if (!shouldUpdateStatus) return;

    try {
      const progressUpdate: SetUserProgressRequest = {
        facts: {
          [factId]: {
            status: 'accuracyPractice'
          }
        }
      };
      await setUserProgress(user.userId, progressUpdate);
    } catch (error) {
      console.error('Failed to update fact status to accuracyPractice:', error);
    }
  };

  const checkAnswer = () => {
    if (!currentQuestion || !userAnswer || isNaN(parseInt(userAnswer, 10))) {
      setIsCorrect(false);
      return;
    }

    const numericAnswer = parseInt(userAnswer, 10);
    const correct = numericAnswer === currentQuestion.result;
    setIsCorrect(correct);

    if (correct) {
      playCorrectSound();
      cardControls.start({ boxShadow: ['0 0 0 0 rgba(34,197,94,0)', '0 0 20px 4px rgba(34,197,94,0.4)', '0 0 0 0 rgba(34,197,94,0)'], transition: { duration: 0.4 } });
    } else {
      cardControls.start({ x: [0, -6, 6, -6, 6, 0], transition: { duration: 0.4 } });
    }

    const timeSpent = Date.now() - startTime;

    // Only call updateFactAttempts which will handle all state updates and API calls
    // Pass the additional condition for green zone
    const updatedCorrectAnswers = updateFactAttempts(correct, timeSpent, isUntimed || currentTimerZone === 'green');

    if (correct) {
      setCurrentQuestion(prev => ({ ...prev!, correctAnswers: updatedCorrectAnswers }));
      if (isMastered(updatedCorrectAnswers, masteryThreshold)) {
        setMasteredQuestions(prev => new Set(prev).add(currentQuestion.id));
      }

      setIsTransitioning(true);
      setTimeout(() => {
        const nextQuestion = getNextQuestion();

        // Reset states
        setUserAnswer('');
        setIsCorrect(null);
        setShowingCorrectAnswer(false);
        setTimerExpired(false);
        setStartTime(Date.now());
        setKey(prev => prev + 1);
        setIsTransitioning(false);
        setCurrentTimerZone('green'); // Reset timer zone for next question

        // Set next question or complete practice
        if (nextQuestion) {
          setCurrentQuestion(nextQuestion);
        } else if (remainingQuestions.length === 0) {
          handlePracticeComplete();
        }
      }, 300);
    } else {
      setCurrentQuestion(prev => ({ ...prev!, correctAnswers: updatedCorrectAnswers }));
      if (!isMastered(updatedCorrectAnswers, masteryThreshold) && masteredQuestions.has(currentQuestion.id)) {
        setMasteredQuestions(prev => {
          const newSet = new Set(prev);
          newSet.delete(currentQuestion.id);
          return newSet;
        });
      }

      setShowingCorrectAnswer(true);
      // Add back to remaining questions
      setRemainingQuestions(prev => [...prev, currentQuestion]);
    }
  };

  const handlePracticeComplete = (progress = factProgressRef.current) => {
    // Check if all facts have reached mastery based on local factProgress state
    const allFactsMastered = generatePermutations(originalFacts).every(fact =>
      (progress[fact.id] || 0) >= masteryThreshold
    );

    if (!allFactsMastered) {
      // If not all facts are mastered, reset remaining questions with non-mastered facts
      const nonMasteredFacts = generatePermutations(originalFacts).filter(fact =>
        (progress[fact.id] || 0) < masteryThreshold
      );
      setRemainingQuestions(nonMasteredFacts);
      const randomIndex = Math.floor(Math.random() * nonMasteredFacts.length);
      const nextQuestion = {
        ...nonMasteredFacts[randomIndex],
        correctAnswers: progress[nonMasteredFacts[randomIndex].id] || 0
      };
      setCurrentQuestion(nextQuestion);
      setUserAnswer('');
      setIsCorrect(null);
      setShowingCorrectAnswer(false);
      setTimerExpired(false);
      setStartTime(Date.now());
      setKey(prev => prev + 1);
      return;
    }

    confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: INDIGO_COLORS });

    // Only navigate when all facts are mastered - pass the original facts
    navigate('/accuracy-practice', {
      state: {
        facts: originalFacts
      }
    });
  };

  // Add this effect to initialize fact attempts tracking
  useEffect(() => {
    const initialFactAttempts: Record<string, { attempts: number; correct: number; timeSpent: number }> = {};
    const initialProgress: Record<string, number> = {};
    const allPermutations = generatePermutations(originalFacts);

    allPermutations.forEach((fact: PermutatedFact) => {
      const trackingFactId = getTrackingFactId(fact);
      initialFactAttempts[trackingFactId] = {
        attempts: 0,
        correct: 0,
        timeSpent: 0
      };
      initialProgress[fact.id] = 0;
    });

    setFactAttempts(initialFactAttempts);
    factProgressRef.current = initialProgress;
    setFactProgress(initialProgress);
  }, [originalFacts]);

  const updateFactAttempts = (correct: boolean, timeSpent: number, isGreenZone = true): number => {
    if (!currentQuestion) return 0;

    // Get the base fact ID
    const baseFactId = currentQuestion.PK.split('FACT#')[1];

    // Update local factAttempts state for tracking purposes
    const trackingFactId = getTrackingFactId(currentQuestion);

    // Use a callback to ensure we have the latest state
    setFactAttempts(prev => {
      const updatedAttempts = {
        ...prev,
        [trackingFactId]: {
          attempts: (prev[trackingFactId]?.attempts || 0) + 1,
          correct: (prev[trackingFactId]?.correct || 0) + (correct ? 1 : 0),
          timeSpent: (prev[trackingFactId]?.timeSpent || 0) + timeSpent
        }
      };

      return updatedAttempts;
    });

    // Calculate new correctAnswers value - only increment if correct AND in green zone (if specified)
    const currentCorrectAnswers = factProgressRef.current[currentQuestion.id] || 0;
    const newCorrectAnswers = getUpdatedDemonstrationCount(
      currentCorrectAnswers,
      correct && isGreenZone,
      user?.iepAccommodationsEnabled === true
    );

    const updatedProgress = {
      ...factProgressRef.current,
      [currentQuestion.id]: newCorrectAnswers
    };
    factProgressRef.current = updatedProgress;
    setFactProgress(updatedProgress);

    void updateProgressForAnswer(
      currentQuestion,
      baseFactId,
      currentCorrectAnswers,
      newCorrectAnswers,
      updatedProgress
    );

    return newCorrectAnswers;
  };

  // Initialize or get next question
  const getNextQuestion = () => {
    if (remainingQuestions.length === 0) {
      return null;
    }
    const randomIndex = Math.floor(Math.random() * remainingQuestions.length);
    const question = remainingQuestions[randomIndex];
    setRemainingQuestions(prev => prev.filter((_, index) => index !== randomIndex));

    // Update the question with its current progress
    return {
      ...question,
      correctAnswers: factProgressRef.current[question.id] || 0
    };
  };

  const handleTryAgain = () => {
    setUserAnswer('');
    setIsCorrect(null);
    setShowingCorrectAnswer(false);
    setTimerExpired(false);
    setStartTime(Date.now());
    setKey(prev => prev + 1);
    // Focus the input after state updates
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  // Add global Enter key handler for Try Again
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !isCorrect && showingCorrectAnswer) {
        handleTryAgain();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isCorrect, showingCorrectAnswer]);

  const handleInputChange = (newValue: string) => {
    if (isCorrect !== null || timerExpired) return;

    setUserAnswer(newValue);

    // Auto-submit when input length matches answer length
    if (newValue.length === currentQuestion?.result.toString().length) {
      const numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && isCorrect === null && !timerExpired) {
        const correct = numValue === currentQuestion.result;

        // Debug logging for state/display mismatch bug
        if (!correct && currentQuestion) {
          const stateQuestion = `${currentQuestion.operand1} ${renderOperationSymbol(currentQuestion.operation)} ${currentQuestion.operand2}`;
          const domQuestionDiv = document.querySelector('[data-testid="question-display"]');
          const domText = domQuestionDiv?.textContent || '';

          // Only log if DOM doesn't match state (actual bug, not user mistake)
          if (!domText.includes(stateQuestion)) {
            console.error('[TimedPractice BUG] State/Display mismatch', {
              stateQuestion,
              domQuestion: domText.trim(),
              userAnswer: numValue,
              expectedResultFromState: currentQuestion.result,
              currentQuestionId: currentQuestion.id,
              timestamp: new Date().toISOString()
            });
          }
        }

        setIsCorrect(correct);

        if (correct) {
          playCorrectSound();
          cardControls.start({ boxShadow: ['0 0 0 0 rgba(34,197,94,0)', '0 0 20px 4px rgba(34,197,94,0.4)', '0 0 0 0 rgba(34,197,94,0)'], transition: { duration: 0.4 } });
        } else {
          cardControls.start({ x: [0, -6, 6, -6, 6, 0], transition: { duration: 0.4 } });
        }

        const timeSpent = Date.now() - startTime;
        const newCorrectAnswers = updateFactAttempts(correct, timeSpent, isUntimed || currentTimerZone === 'green');

        if (correct) {
          setCurrentQuestion(prev => ({
            ...prev!,
            correctAnswers: newCorrectAnswers
          }));

          if (isMastered(newCorrectAnswers, masteryThreshold)) {
            setMasteredQuestions(prev => new Set(prev).add(currentQuestion.id));
          }

          setIsTransitioning(true);
          setTimeout(() => {
            const nextQuestion = getNextQuestion();

            // Reset states
            setUserAnswer('');
            setIsCorrect(null);
            setShowingCorrectAnswer(false);
            setTimerExpired(false);
            setStartTime(Date.now());
            setKey(prev => prev + 1);
            setIsTransitioning(false);
            setCurrentTimerZone('green'); // Reset timer zone for next question

            // Set next question or complete practice
            if (nextQuestion) {
              setCurrentQuestion(nextQuestion);
            } else if (remainingQuestions.length === 0) {
              handlePracticeComplete();
            }
          }, 300);
        } else {
          setCurrentQuestion(prev => ({
            ...prev!,
            correctAnswers: newCorrectAnswers
          }));

          // Remove from mastered if it was mastered
          if (masteredQuestions.has(currentQuestion.id)) {
            setMasteredQuestions(prev => {
              const newSet = new Set(prev);
              newSet.delete(currentQuestion.id);
              return newSet;
            });
          }

          setShowingCorrectAnswer(true);
          // Add back to remaining questions
          setRemainingQuestions(prev => [...prev, currentQuestion]);
        }
      }
    }
  };

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 flex flex-col justify-center p-6">
        <div className="text-center animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 flex flex-col justify-center p-6">
      <BackButton onBack={() => navigate('/practice', { state: { facts: originalFacts } })} />
      <div className="max-w-6xl mx-auto space-y-8">
        {isInitialized && (
          <Timer
            duration={getAccommodationTimerDuration(
              getTimerDuration(factProgress[currentQuestion?.id || ''] || 0),
              user?.iepAccommodationsEnabled,
              user?.iepTimerMode
            )}
            key={key}
            onTimeout={() => {
              setTimerExpired(true);
              setIsCorrect(false);
              setShowingCorrectAnswer(true);
              if (currentQuestion) {
                updateFactAttempts(false, Date.now() - startTime, false);
              }
            }}
            onZoneChange={(zone) => setCurrentTimerZone(zone)}
            isActive={isCorrect === null && !showingCorrectAnswer && !timerExpired}
            untimed={isUntimed}
          />
        )}

        <h1 className="text-4xl font-bold text-center mb-6 animate-fade-in flex items-center justify-center gap-4">
          <Zap className="text-yellow-400 w-8 h-8" />
          <span className="bg-gradient-to-r from-indigo-600 to-purple-600 text-transparent bg-clip-text">
            Practice Time!
          </span>
        </h1>

        <div className="flex flex-wrap justify-center gap-3 mb-8 max-w-[640px] mx-auto">
          {generatePermutations(originalFacts).map((fact) => (
            <FactMasteryCard
              key={fact.id}
              masteryThreshold={masteryThreshold}
              fact={{
                ...fact,
                correctAnswers: factProgress[fact.id] || 0
              }}
            />
          ))}
        </div>

        <div className="flex gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1 relative"
          >
            <motion.div
              animate={cardControls}
              className="h-full space-y-6 p-6 md:p-8 bg-white rounded-2xl shadow-lg shadow-indigo-100/50 border border-indigo-100 relative overflow-hidden"
            >
            <div className="text-center space-y-6">
              <div data-testid="question-display" className="text-4xl font-bold text-gray-900 py-8">
                {currentQuestion.operand1} {renderOperationSymbol(currentQuestion.operation)} {currentQuestion.operand2} =
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  value={userAnswer}
                  onChange={(e) => handleInputChange(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  className="ml-4 w-32 text-center border-b-2 border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-all"
                  disabled={isCorrect !== null || timerExpired}
                />
              </div>

              {!isCorrect && isCorrect !== null && (
                <div className="space-y-4">
                  <div className="text-xl font-bold text-red-500">
                    Try again
                  </div>
                  {showingCorrectAnswer && (
                    <div className="text-lg font-medium text-gray-700 py-4">
                      The correct fact is: {currentQuestion.operand1} {renderOperationSymbol(currentQuestion.operation)} {currentQuestion.operand2} = {currentQuestion.result}
                    </div>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleTryAgain}
                    className="w-full px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold text-base shadow-lg shadow-indigo-200/50 hover:shadow-lg transition-all duration-300"
                  >
                    Try Again
                  </motion.button>
                </div>
              )}

              {isCorrect && (
                <>
                  <div className="text-xl font-bold text-green-500">
                    Correct!
                  </div>
                  <div className="h-1 w-full bg-gray-100 rounded overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 0.3, ease: "linear" }}
                      className="h-full bg-green-500"
                    />
                  </div>
                </>
              )}
            </div>
            </motion.div>
          </motion.div>

          {/* Touchpad Card */}
          {hasTouchScreen && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-6 bg-white rounded-2xl shadow-lg shadow-indigo-100/50 border border-indigo-100"
            >
              <TouchpadInput
                value={userAnswer}
                onChange={handleInputChange}
                disabled={isCorrect !== null || timerExpired}
                showFeedback={isCorrect !== null}
              />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
